import { faBox, faBoxOpen, faGift, faMoneyBill, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Product } from '@shared/components/ui';
import { Badge } from '@shared/components/ui';
import { useAuthStore } from '@shared/stores/authStore';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PackagingItem } from '../../models/Packaging';
import { DropdownActions } from '../interaction';

interface PackagingCardProps {
  item: PackagingItem;
  onClick?: (item: PackagingItem) => void;
  onEdit?: (item: PackagingItem) => void;
  onDelete?: (item: PackagingItem) => void;
  onAdjustStock?: (item: PackagingItem) => void;
  className?: string;
}

const PackagingCard = React.memo<PackagingCardProps>(
  ({ item, onClick, onEdit, onDelete, onAdjustStock, className = '' }) => {
    const { t } = useTranslation('packagingModule');
    const getTeamType = useAuthStore((state) => state.getTeamType);
    const isTenant3pl = getTeamType() === 'tenant_3pl';

    const currency = useCurrency();

    const handleCardClick = useCallback(() => {
      onClick?.(item);
    }, [onClick, item]);

    const getCategoryLabel = () => {
      switch (item.category) {
        case 'own_box':
          return t('card.category_own_box');
        case 'customer_box':
          return t('card.category_customer_box');
        case 'marketing_item':
          return t('card.category_marketing_item');
        default:
          return item.category;
      }
    };

    const getCategoryIcon = () => {
      if (item.category === 'marketing_item') {
        return faGift;
      }
      return faBox;
    };

    const getCategoryBadgeClass = () => {
      if (item.category === 'marketing_item') {
        return 'bg-bg-info text-info';
      }
      return 'bg-bg-warning text-warning';
    };

    const getBoxGroupLabel = () => {
      if (!item.packaging_box_group_id) {
        return t('card.box_group_unassigned');
      }

      if (item.box_group?.name) {
        return item.box_group.name;
      }

      return t('card.box_group_fallback', { id: item.packaging_box_group_id });
    };

    return (
      <div
        className={`
        dark:bg-card-bg border border-card-border rounded-xl p-4 bg-body-bg
        transition-colors
        ${className}
      `}
        onClick={handleCardClick}
        data-testid={`packaging-card-${item.id}`}
      >
        {/* Información principal usando componente Product */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-1">
              <Badge
                variant="transparent-blue"
                className={`px-2 py-[1px] text-xs w-fit font-normal rounded-full border-0 flex items-center gap-1 ${getCategoryBadgeClass()}`}
              >
                <FontAwesomeIcon icon={getCategoryIcon()} className="w-3.5 h-3.5" />
                <span>{getCategoryLabel()}</span>
              </Badge>

              {item.category === 'customer_box' && (
                <Badge
                  variant="transparent-blue"
                  className="px-2 py-[1px] text-xs w-fit font-normal rounded-full border-0 flex items-center gap-1 bg-bg-info text-info"
                >
                  <FontAwesomeIcon icon={faBoxOpen} className="w-3.5 h-3.5" />
                  <span>{getBoxGroupLabel()}</span>
                </Badge>
              )}
            </div>
            <Product
              id={item.id}
              src={item.image_url || ''}
              name={item.name}
              code={item.code}
              acronym={item.name?.charAt(0).toUpperCase()}
              enableLineClamp={true}
              showImageModal={true}
              data-testid={`packaging-name-${item.id}`}
            />
          </div>

          <div className="flex items-start gap-2">
            <DropdownActions item={item} onEdit={onEdit} onDelete={onDelete} onAdjustStock={onAdjustStock} />

            {/* Badge de estado inactivo */}
            {item.status === 'inactive' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-bg-warning text-warning mt-1 inline-block">
                {t('card.inactive')}
              </span>
            )}
          </div>
        </div>

        {/* Contenido de la tarjeta */}
        <div className="space-y-2 mt-3">
          {(item.pvp !== null && item.pvp !== undefined) || isTenant3pl ? (
            <div className="flex items-center gap-4 text-sm">
              {/* PVP si existe */}
              {item.pvp !== null && item.pvp !== undefined && (
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faMoneyBill} className="mr-2 w-3.5 h-3.5" />
                  <span className="text-complementary-text text-xs">{formatCurrencyAmount(item.pvp, currency)}</span>
                </div>
              )}

              {/* Cliente visible solo para tenant_3pl */}
              {isTenant3pl && (
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faUser} className="mr-2 w-3.5 h-3.5" />
                  <span className="text-complementary-text text-xs">{item.team?.name ?? '-'}</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

PackagingCard.displayName = 'PackagingCard';

export default PackagingCard;
